/* eslint-disable max-lines */
import React, {type ReactElement} from 'react';
import {Link} from 'react-router-dom';
import SpriteIcon from 'app/utils/sprites/SpriteIcon';

interface DesktopTopHeaderProps {
    title: string
    blinds: string
    balanceLabel: string
    depositHref: string
    onClose: () => void
    onDragStart: (event: React.PointerEvent<HTMLDivElement>) => void
    maximizeHref?: string
    onMaximize?: () => void
    onMinimize?: () => void
    isDraggable?: boolean
}

interface MobileTopHeaderProps {
    title: string
    blinds: string
    balanceLabel: string
    onClose: () => void
}

interface DesktopSecondaryHeaderProps {
    onLeaveTable: () => void
}

interface MobileSecondaryHeaderProps {
    onLeaveTable: () => void
}

interface HeaderButtonProps {
    className: string
    icon: React.ReactNode
    label?: string
    onClick?: () => void
    style?: React.CSSProperties
    title?: string
}

const settingsIconPath = [
    'M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06',
    'a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09',
    'A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83',
    'l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09',
    'A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83',
    'l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09',
    'a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83',
    'l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09',
    'a1.65 1.65 0 0 0-1.51 1z'
].join(' ');

const PlusIcon = ({size}: {size: number}): React.ReactElement => <svg height={size} viewBox="0 0 24 24" width={size}>
    <circle cx="12" cy="12" fill="none" r="10" stroke="currentColor" strokeWidth="1.5" />
    <path d="M12 7v10M7 12h10" stroke="currentColor" strokeLinecap="round" strokeWidth="1.5" />
</svg>;

const PauseIcon = ({size}: {size: number}): React.ReactElement => <svg height={size} viewBox="0 0 24 24" width={size}>
    <rect fill="currentColor" height="16" width="4" x="6" y="4" />
    <rect fill="currentColor" height="16" width="4" x="14" y="4" />
</svg>;

const LeaveIcon = ({size}: {size: number}): React.ReactElement => <svg fill="none" height={size} viewBox="0 0 24 24" width={size}>
    <path
        d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9"
        stroke="currentColor"
        strokeLinecap="round"
        strokeWidth="1.5"
    />
</svg>;

const HistoryIcon = ({size}: {size: number}): React.ReactElement => <svg fill="none" height={size} viewBox="0 0 24 24" width={size}>
    <path
        d="M12 8v4l3 3m6-3a9 9 0 1 1-18 0 9 9 0 0 1 18 0z"
        stroke="currentColor"
        strokeLinecap="round"
        strokeWidth="1.5"
    />
</svg>;

const SoundIcon = ({size}: {size: number}): React.ReactElement => <svg fill="none" height={size} viewBox="0 0 24 24" width={size}>
    <path
        d="M11 5L6 9H2v6h4l5 4V5zM15.54 8.46a5 5 0 0 1 0 7.07"
        stroke="currentColor"
        strokeLinecap="round"
        strokeWidth="1.5"
    />
</svg>;

const SettingsIcon = ({size}: {size: number}): React.ReactElement => <svg
    fill="none"
    height={size}
    stroke="currentColor"
    strokeLinecap="round"
    strokeWidth="1.8"
    viewBox="0 0 24 24"
    width={size}
>
    <circle cx="12" cy="12" r="3" />
    <path d={settingsIconPath} />
</svg>;

const BackIcon = (): React.ReactElement => <svg
    fill="none"
    height="18"
    stroke="currentColor"
    strokeLinecap="round"
    strokeWidth="2"
    viewBox="0 0 24 24"
    width="18"
>
    <path d="M19 12H5M12 19l-7-7 7-7" />
</svg>;

const HeaderButton = ({
    className,
    icon,
    label,
    onClick,
    style,
    title
}: HeaderButtonProps): React.ReactElement => <button className={className} onClick={onClick} style={style} title={title} type="button">
    {icon}
    {label}
</button>;

export const widgetShellStyles = `
    .poker-widget,
    .poker-widget-mobile {
        background: #141414;
        font-family: Roboto, Arial, sans-serif;
        overflow: hidden;
        pointer-events: auto !important;
        user-select: none;
    }

    .poker-widget {
        border: 1px solid #555;
        border-radius: 6px;
        box-shadow: 0 8px 32px rgba(0, 0, 0, 0.6);
        box-sizing: border-box;
        display: flex;
        flex-direction: column;
        position: relative;
    }

    .proom-widget-iframe-host {
        flex: 1 1 auto;
        max-width: 100%;
        min-height: 0;
        min-width: 0;
        overflow: hidden;
        width: 100%;
    }

    .proom-widget-iframe-host iframe {
        border: none;
        display: block;
        height: 100%;
        pointer-events: auto !important;
        width: 100%;
    }

    .proom-widget-resize-handle {
        background: transparent;
        bottom: 0;
        cursor: nwse-resize;
        height: 16px;
        position: absolute;
        right: 0;
        touch-action: none;
        width: 16px;
        z-index: 2;
    }

    .proom-widget-resize-handle::after {
        border-bottom: 2px solid #666;
        border-right: 2px solid #666;
        bottom: 4px;
        content: "";
        height: 8px;
        position: absolute;
        right: 4px;
        width: 8px;
    }

    .proom-widget-resize-handle:hover::after {
        border-color: #aaa;
    }

    .poker-widget-mobile {
        border: 1px solid #555;
        border-radius: 6px;
        box-shadow: 0 8px 32px rgba(0, 0, 0, 0.6);
        width: 390px;
    }

    .proom-widget-desktop-frame {
        max-height: 100vh;
        max-width: 100vw;
        pointer-events: auto !important;
        position: fixed;
        z-index: 9000;
    }

    .proom-widget-mobile-frame {
        border: none;
        border-radius: 0;
        box-shadow: none;
        height: 100vh;
        width: 100vw;
    }

    .egb-header {
        align-items: center;
        background: #1e1e1e;
        border-bottom: 1px solid #333;
        cursor: grab;
        display: flex;
        flex-shrink: 0;
        gap: 8px;
        height: 36px;
        padding: 0 8px 0 10px;
    }

    .egb-header:active {
        cursor: grabbing;
    }

    .egb-header--static {
        cursor: default;
        padding-left: 10px;
    }

    .egb-header--static:active {
        cursor: default;
    }

    .egb-header--static .drag-handle {
        display: none;
    }

    .drag-handle {
        cursor: grab;
        display: flex;
        flex-direction: column;
        flex-shrink: 0;
        gap: 3px;
        opacity: 0.45;
        padding: 2px 4px;
        transition: opacity 0.15s;
    }

    .drag-handle:hover {
        opacity: 1;
    }

    .drag-handle span {
        background: #aaa;
        border-radius: 1px;
        display: block;
        height: 2px;
        width: 14px;
    }

    .egb-table-info,
    .egb-table-info-mobile {
        align-items: center;
        display: flex;
        flex: 1;
        gap: 8px;
        min-width: 0;
        overflow: hidden;
    }

    .egb-table-info-mobile {
        gap: 6px;
    }

    .egb-table-name,
    .egb-table-name-mobile {
        color: #ddd;
        font-size: 12px;
        font-weight: 700;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    .egb-blinds,
    .egb-blinds-mobile {
        color: #fe9400;
        font-size: 11px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    .egb-header-right {
        align-items: center;
        display: flex;
        gap: 6px;
        margin-left: auto;
    }

    .egb-balance {
        color: #ccc;
        font-size: 12px;
        padding-right: 4px;
        white-space: nowrap;
    }

    .egb-balance strong,
    .egb-balance-mobile {
        color: #ffe082;
        font-weight: 700;
    }

    .egb-balance-mobile {
        flex-shrink: 0;
        font-size: 12px;
        white-space: nowrap;
    }

    .egb-deposit-btn {
        align-items: center;
        background: #fe9400;
        border: none;
        border-radius: 3px;
        color: #000;
        cursor: pointer;
        display: inline-flex;
        font-size: 11px;
        font-weight: 700;
        height: 24px;
        padding: 0 10px;
        text-decoration: none;
        white-space: nowrap;
    }

    .egb-deposit-btn:hover {
        background: #ffa726;
    }

    .egb-close-btn,
    .egb-maximize-btn,
    .back-btn {
        align-items: center;
        background: none;
        border: none;
        border-radius: 4px;
        color: #888;
        cursor: pointer;
        display: flex;
        justify-content: center;
        padding: 0;
        transition: background 0.15s, color 0.15s;
    }

    .egb-close-btn,
    .egb-maximize-btn {
        font-size: 16px;
        height: 24px;
        width: 24px;
    }

    .egb-maximize-btn {
        text-decoration: none;
    }

    .egb-maximize-btn svg {
        margin: 0;
    }

    .egb-maximize-btn:hover {
        background: #333;
        color: #ddd;
    }

    .egb-close-btn:hover {
        background: #c0392b;
        color: #fff;
    }

    .egb-header-mobile {
        align-items: center;
        background: #1e1e1e;
        border-bottom: 1px solid #333;
        display: flex;
        gap: 6px;
        height: 36px;
        padding: 0 8px 0 6px;
    }

    .back-btn {
        color: #aaa;
        flex-shrink: 0;
        height: 30px;
        width: 30px;
    }

    .back-btn:hover {
        background: #2a2a2a;
        color: #fff;
    }

    .proom-header,
    .proom-header-mobile {
        align-items: center;
        background: #141414;
        border-bottom: 1px solid #444;
        display: flex;
    }

    .proom-header {
        height: 33px;
    }

    .proom-header-mobile {
        height: 32px;
    }

    .menu-btn,
    .menu-btn-m {
        align-items: center;
        background: none;
        border: none;
        border-right: 1px solid #333;
        color: #bbb;
        cursor: pointer;
        display: flex;
        flex-shrink: 0;
        transition: background 0.15s, color 0.15s;
    }

    .menu-btn:last-child,
    .menu-btn-m:last-child {
        border-right: none;
    }

    .menu-btn {
        gap: 5px;
        height: 33px;
        padding: 0 10px;
        white-space: nowrap;
    }

    .menu-btn-m {
        flex: 1;
        height: 32px;
        justify-content: center;
        padding: 0;
    }

    .menu-btn:hover,
    .menu-btn-m:hover {
        background: #222;
        color: #fff;
    }

    .menu-btn.warn:hover {
        background: #3a1a1a;
        color: #e57373;
    }

    .menu-btn.active,
    .menu-btn-m.active {
        color: #fe9400;
    }

    .poker-widget.proom-widget-desktop-frame .poker {
        height: auto;
        width: 100%;
    }

    @media (max-width: 480px) {
        .proom-widget-mobile-frame {
            padding-top: 60px;
        }
    }
`;

export const ProomPokerDesktopTopHeader = ({
    title,
    blinds,
    balanceLabel,
    depositHref,
    onClose,
    onDragStart,
    maximizeHref,
    onMaximize,
    onMinimize,
    isDraggable = true
}: DesktopTopHeaderProps): React.ReactElement => <div
    className={isDraggable ? 'egb-header' : 'egb-header egb-header--static'}
    {...(isDraggable ? {onPointerDown: onDragStart} : {})}
>
    <div className="drag-handle" aria-hidden="true">
        <span />
        <span />
        <span />
    </div>
    <div className="egb-table-info">
        <span className="egb-table-name">{title}</span>
        {blinds && <span className="egb-blinds">{blinds}</span>}
    </div>
    <div className="egb-header-right">
        <div className="egb-balance">
                Баланс: <strong>{balanceLabel}</strong>
        </div>
        <a className="egb-deposit-btn" data-no-drag="true" href={depositHref}>
                + Депозит
        </a>
        {onMinimize
            ? <button
                className="egb-maximize-btn"
                data-no-drag="true"
                onClick={onMinimize}
                title="Свернуть"
                type="button"
            >
                <SpriteIcon name="#svg-maximize" size={{height: 16, width: 16}}/>
            </button>
            : null}
        {maximizeHref
            ? <Link
                className="egb-maximize-btn"
                data-no-drag="true"
                onClick={onMaximize}
                rel="noopener"
                target="_blank"
                title="Развернуть"
                to={maximizeHref}
            >
                <SpriteIcon name="#svg-maximize" size={{height: 16, width: 16}}/>
            </Link>
            : null}
        <button
            className="egb-close-btn"
            data-no-drag="true"
            onClick={onClose}
            title="Закрыть"
            type="button"
        >
                ✕
        </button>
    </div>
</div>;

export const ProomPokerDesktopSecondaryHeader = ({onLeaveTable}: DesktopSecondaryHeaderProps): React.ReactElement => <div className="proom-header">
    <HeaderButton
        className="menu-btn active"
        icon={<PlusIcon size={14} />}
        label="Докупить"
        style={{borderLeft: 'none'}}
    />
    <HeaderButton className="menu-btn" icon={<PauseIcon size={14} />} label="Пропустить" />
    <HeaderButton className="menu-btn warn" icon={<LeaveIcon size={14} />} label="Покинуть" onClick={onLeaveTable} />
    <HeaderButton className="menu-btn" icon={<HistoryIcon size={14} />} label="История" />
    <HeaderButton className="menu-btn" icon={<SoundIcon size={14} />} label="Звук" />
    <HeaderButton className="menu-btn" icon={<SettingsIcon size={14} />} />
</div>;

export const ProomPokerMobileTopHeader = ({
    title,
    blinds,
    balanceLabel,
    onClose
}: MobileTopHeaderProps): React.ReactElement => <div className="egb-header-mobile">
    <button className="back-btn" onClick={onClose} title="Назад" type="button">
        <BackIcon />
    </button>
    <div className="egb-table-info-mobile">
        <span className="egb-table-name-mobile">{title}</span>
        {blinds && <span className="egb-blinds-mobile">{blinds}</span>}
    </div>
    <span className="egb-balance-mobile">{balanceLabel}</span>
</div>;

export const ProomPokerMobileSecondaryHeader = ({onLeaveTable}: MobileSecondaryHeaderProps): ReactElement => <div className="proom-header-mobile">
    <HeaderButton className="menu-btn-m active" icon={<PlusIcon size={17} />} title="Докупить" />
    <HeaderButton className="menu-btn-m" icon={<PauseIcon size={17} />} title="Пропустить" />
    <HeaderButton className="menu-btn-m" icon={<LeaveIcon size={17} />} onClick={onLeaveTable} title="Покинуть стол" />
    <HeaderButton className="menu-btn-m" icon={<HistoryIcon size={17} />} title="История раздач" />
    <HeaderButton className="menu-btn-m" icon={<SoundIcon size={17} />} title="Звук" />
    <HeaderButton className="menu-btn-m" icon={<SettingsIcon size={17} />} title="Настройки" />
</div>;
